import { useTranslation } from "@sagot/i18n";
import { Icon, Input, Label, Segmented, Select, Switch } from "@sagot/ui";
import { Controller, type Control, type UseFormRegister } from "react-hook-form";
import type { AgentSummary } from "../shared/use-engage-data";
import { ALL_AGENTS, type ComposerValues } from "./composer-form";
import { EXPIRY_OPTIONS } from "./schedule";

interface TimingProps {
  control: Control<ComposerValues>;
  register: UseFormRegister<ComposerValues>;
  when: ComposerValues["when"];
  timeZone: string;
  scheduleError?: string;
}

/** "When to send": Send now | Schedule (date + time in the workspace time zone). */
export function WhenSection({ control, register, when, timeZone, scheduleError }: TimingProps) {
  const { t } = useTranslation();
  return (
    <div>
      <div className="label">{t("engage.announcements.composer.when")}</div>
      <Controller
        control={control}
        name="when"
        render={({ field }) => (
          <Segmented
            aria-label={t("engage.announcements.composer.when")}
            value={field.value}
            onValueChange={field.onChange}
            options={[
              { value: "now", label: t("engage.announcements.composer.sendNow") },
              { value: "later", label: t("engage.announcements.composer.schedule") },
            ]}
          />
        )}
      />
      {when === "later" ? (
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="ann-date">{t("engage.announcements.composer.date")}</Label>
            <Input id="ann-date" type="date" invalid={Boolean(scheduleError)} {...register("date")} />
          </div>
          <div>
            <Label htmlFor="ann-time">{t("engage.announcements.composer.time")}</Label>
            <Input id="ann-time" type="time" invalid={Boolean(scheduleError)} {...register("time")} />
          </div>
          <p className={scheduleError ? "col-span-2 text-xs font-semibold text-danger" : "col-span-2 text-[11px] text-fg-subtle"} role={scheduleError ? "alert" : undefined}>
            {scheduleError ?? t("engage.announcements.composer.timeZoneHint", { zone: timeZone })}
          </p>
        </div>
      ) : null}
    </div>
  );
}

interface TeachProps {
  control: Control<ComposerValues>;
  register: UseFormRegister<ComposerValues>;
  teach: boolean;
  agents: readonly AgentSummary[];
}

/** "Also teach the assistant": temporary knowledge source applied at send time (DEC-031). */
export function TeachSection({ control, register, teach, agents }: TeachProps) {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-line bg-primary-soft p-4">
      <div className="flex items-start gap-3">
        <Icon name="brain" className="mt-0.5 size-5 text-primary-ink" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm font-bold" id="ann-teach-title">
              {t("engage.announcements.composer.teachTitle")}
            </span>
            <Controller
              control={control}
              name="teach"
              render={({ field }) => <Switch checked={field.value} onCheckedChange={field.onChange} aria-labelledby="ann-teach-title" />}
            />
          </div>
          <p className="mt-1 text-xs text-fg-muted">{t("engage.announcements.composer.teachHelp")}</p>
          {teach ? (
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ann-expiry">{t("engage.announcements.composer.forgetAfter")}</Label>
                <Select id="ann-expiry" className="h-9" {...register("expiry")}>
                  {EXPIRY_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {t(`engage.announcements.composer.expiry.${option}`)}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <Label htmlFor="ann-agent">{t("engage.announcements.composer.agents")}</Label>
                <Select id="ann-agent" className="h-9" {...register("agent")}>
                  <option value={ALL_AGENTS}>{t("engage.allAgents")}</option>
                  {agents.map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.name}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
